import {
  useCallback,
  useEffect,
  type MutableRefObject,
} from "react";
import type { ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import { SessionUploadProductText } from "@cabal/shared/domain/upload/enums.js";
import type {
  GetAncestorChain,
  IsInUserAttachmentsSubtree,
} from "./session-live-signals.types";

interface UseSessionLiveProductScopeInput {
  products: Record<string, ProductSummary>;
  productsRef: MutableRefObject<Record<string, ProductSummary>>;
  rootProductIdRef: MutableRefObject<string | null>;
  userAttachmentsProductIdRef: MutableRefObject<string | null>;
}

interface SessionLiveProductScope {
  getAncestorChain: GetAncestorChain;
  isInUserAttachmentsSubtree: IsInUserAttachmentsSubtree;
}

export function useSessionLiveProductScope({
  products,
  productsRef,
  rootProductIdRef,
  userAttachmentsProductIdRef,
}: UseSessionLiveProductScopeInput): SessionLiveProductScope {
  const getAncestorChain = useCallback(
    (productId: string): string[] => {
      const chain: string[] = [];
      let current = productsRef.current[productId];
      while (current?.parentProductId) {
        chain.push(current.parentProductId);
        current = productsRef.current[current.parentProductId];
      }
      return chain;
    },
    [productsRef],
  );

  const isInUserAttachmentsSubtree = useCallback(
    (productId: string): boolean => {
      const targetId = userAttachmentsProductIdRef.current;
      if (!targetId) return false;
      if (productId === targetId) return true;
      return getAncestorChain(productId).includes(targetId);
    },
    [getAncestorChain, userAttachmentsProductIdRef],
  );

  useEffect(() => {
    if (userAttachmentsProductIdRef.current != null) return;
    const rootId = rootProductIdRef.current;
    if (!rootId) return;

    const userAttachmentsProduct = Object.values(products).find(
      (product) =>
        product.parentProductId === rootId &&
        product.name === SessionUploadProductText.UserAttachmentsCollectionName,
    );
    if (!userAttachmentsProduct) return;

    userAttachmentsProductIdRef.current = userAttachmentsProduct.id;
  }, [products, rootProductIdRef, userAttachmentsProductIdRef]);

  return { getAncestorChain, isInUserAttachmentsSubtree };
}
